import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, Save, Check } from 'lucide-react';

const EMPTY = {
  setting_key: 'global',
  website_url: '',
  email: '',
  country_code: 'GB',
  phone_number: '',
  live_chat_available: false,
  chatbot_available: false,
  chat_url: '',
  hours: '',
};

const inputCls = 'bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-primary/50 transition-colors w-full';

// The customer service contact details, exactly as they were before this page
// gained tabs — they power the public /customer-service page and the Google
// Merchant Center customer service attributes.
export default function CustomerServiceDetailsForm() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');
  const [recordId, setRecordId] = useState(null);

  const { data: settingsList = [], isLoading } = useQuery({
    queryKey: ['customer-service-settings'],
    queryFn: () => base44.entities.CustomerServiceSettings.list(),
  });

  useEffect(() => {
    const rec = (settingsList || []).find((x) => x.setting_key === 'global');
    if (rec) {
      setForm({ ...EMPTY, ...rec });
      setRecordId(rec.id);
    }
  }, [settingsList]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setErr('');
    setSaving(true);
    try {
      if (recordId) {
        await base44.entities.CustomerServiceSettings.update(recordId, form);
      } else {
        const created = await base44.entities.CustomerServiceSettings.create(form);
        setRecordId(created.id);
      }
      setSavedAt(Date.now());
      queryClient.invalidateQueries({ queryKey: ['customer-service-settings'] });
    } catch (ex) {
      setErr(ex.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-5">
      <Field label="Website URL (customer service page)">
        <input className={inputCls} value={form.website_url} onChange={(e) => set('website_url', e.target.value)} placeholder="https://motionreality.co.uk/customer-service" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Email">
          <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="sales@motionreality.co.uk" />
        </Field>
        <Field label="Country Code">
          <input className={inputCls} value={form.country_code} onChange={(e) => set('country_code', e.target.value.toUpperCase())} placeholder="GB" maxLength={2} />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Phone Number (international)">
          <input className={inputCls} value={form.phone_number} onChange={(e) => set('phone_number', e.target.value)} placeholder="+44 151 528 3001" />
        </Field>
        <Field label="Opening Hours">
          <input className={inputCls} value={form.hours} onChange={(e) => set('hours', e.target.value)} placeholder="Mon–Fri 9am–6pm · Sat 10am–4pm" />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Toggle
          label="Live chat support available"
          desc="Show live chat as available on the customer service page"
          on={!!form.live_chat_available}
          onToggle={() => set('live_chat_available', !form.live_chat_available)}
        />
        <Toggle
          label="Chat bot support available"
          desc="Show chatbot support as available on the customer service page"
          on={!!form.chatbot_available}
          onToggle={() => set('chatbot_available', !form.chatbot_available)}
        />
      </div>

      {form.live_chat_available && (
        <Field label="Live chat URL / instructions (optional)">
          <input className={inputCls} value={form.chat_url} onChange={(e) => set('chat_url', e.target.value)} placeholder="https://... or in-store instructions" />
        </Field>
      )}

      {err && <p className="text-red-400 text-xs">{err}</p>}

      <div className="flex items-center gap-3 pt-3 border-t border-white/5">
        <button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-6 py-3 transition-colors flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'SAVING...' : 'SAVE SETTINGS'}
        </button>
        {savedAt && !err && (
          <span className="flex items-center gap-1.5 text-green-400 text-xs">
            <Check className="w-3.5 h-3.5" /> Saved
          </span>
        )}
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-white/40 text-[10px] font-bold tracking-widest uppercase mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, desc, on, onToggle }) {
  return (
    <button type="button" onClick={onToggle} className={`text-left border p-4 transition-colors ${on ? 'border-primary/40 bg-primary/5' : 'border-white/10 bg-[#111115] hover:border-white/20'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-white font-bold text-sm">{label}</span>
        <span className={`w-10 h-5 rounded-full relative transition-colors ${on ? 'bg-primary' : 'bg-white/10'}`}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? 'left-5' : 'left-0.5'}`} />
        </span>
      </div>
      <p className="text-white/40 text-xs">{desc}</p>
    </button>
  );
}